import type { ConversationTimelineEventDto, MessageDto } from "@multi-chat/shared";
import { compareMessagesChronological, isOutboundMessage } from "./inbox-display.js";

export type ConversationTimelineItem =
  | { kind: "message"; message: MessageDto }
  | { kind: "event"; event: ConversationTimelineEventDto };

function messageSortAtMs(message: MessageDto): number {
  return Date.parse(message.platformSentAt ?? message.createdAt);
}

function eventSortAtMs(event: ConversationTimelineEventDto): number {
  return Date.parse(event.occurredAt);
}

/**
 * Merge messages (chronological) with ConversationEvent rows.
 * Equal timestamps: message first, then event id order (C-6 / mergeConversationTimeline).
 */
export function mergeConversationTimeline(
  messagesChronological: readonly MessageDto[],
  events: readonly ConversationTimelineEventDto[],
): ConversationTimelineItem[] {
  const items: ConversationTimelineItem[] = [
    ...messagesChronological.map((message) => ({ kind: "message" as const, message })),
    ...events.map((event) => ({ kind: "event" as const, event })),
  ];

  return items.toSorted((a, b) => {
    const aMs = a.kind === "message" ? messageSortAtMs(a.message) : eventSortAtMs(a.event);
    const bMs = b.kind === "message" ? messageSortAtMs(b.message) : eventSortAtMs(b.event);
    if (aMs !== bMs) return aMs - bMs;
    if (a.kind !== b.kind) {
      // Message before event on equal timestamps.
      return a.kind === "message" ? -1 : 1;
    }
    if (a.kind === "message" && b.kind === "message") {
      return compareMessagesChronological(a.message, b.message);
    }
    if (a.kind === "event" && b.kind === "event") {
      const aId = BigInt(a.event.eventId);
      const bId = BigInt(b.event.eventId);
      if (aId === bId) return 0;
      return aId < bId ? -1 : 1;
    }
    return 0;
  });
}

/** Deduplicate events by id when a refreshed newest page overlaps loaded events. */
export function mergeTimelineEvents(
  previous: readonly ConversationTimelineEventDto[],
  incoming: readonly ConversationTimelineEventDto[],
): ConversationTimelineEventDto[] {
  if (incoming.length === 0) return [...previous];
  const byId = new Map<string, ConversationTimelineEventDto>();
  for (const event of previous) {
    byId.set(event.eventId, event);
  }
  for (const event of incoming) {
    byId.set(event.eventId, event);
  }
  return [...byId.values()].toSorted((a, b) => {
    const delta = Date.parse(a.occurredAt) - Date.parse(b.occurredAt);
    if (delta !== 0) return delta;
    const aId = BigInt(a.eventId);
    const bId = BigInt(b.eventId);
    if (aId === bId) return 0;
    return aId < bId ? -1 : 1;
  });
}

export function isInboundCustomerMessage(message: MessageDto): boolean {
  return !isOutboundMessage(message);
}
